import { useCallback, useEffect, useMemo, useState } from "react";
import { AccountControllerService } from "@/api";
import useQuery from "@/hooks/useQuery";
import { IAccount } from "@/types/api";

const sortAccountsByName = (accounts: IAccount[]): IAccount[] =>
  [...accounts].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

const toAccountPayload = (accounts: IAccount[]) =>
  accounts.map(account => ({
    account_id: account.id as string,
    account_type: account.type as string,
    account_name: account.name as string,
  }));

/**
 * Custom hook to manage account selection dropdown
 * Fetches accounts with Guardian Agent enabled from the backend API
 */
export const useAccountSelection = () => {
  // Fetch accounts with guardianAgentEnabled=true from the API
  const { data: accounts = [] } = useQuery({
    method: AccountControllerService.getAccountBasedOnDataSourceUsingGet,
    params: {
      guardianAgentEnabled: true,
    },
    options: {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    },
  });

  const orderAccounts = useMemo(() => sortAccountsByName(accounts), [accounts]);
  const [selectedAccounts, setSelectedAccounts] = useState<IAccount[]>([]);

  useEffect(() => {
    if (orderAccounts.length === 0) {
      setSelectedAccounts(prev => (prev.length === 0 ? prev : []));
      return;
    }

    setSelectedAccounts(prev => {
      if (
        prev.length === 1 &&
        orderAccounts.some(account => account.id === prev[0]?.id)
      ) {
        return prev;
      }
      return [orderAccounts[0]];
    });
  }, [orderAccounts]);

  const handleAccountSelect = useCallback(
    (nextAccounts: IAccount[]) => {
      const single = nextAccounts.slice(0, 1);
      if (single.length === 0) {
        setSelectedAccounts(orderAccounts.length > 0 ? [orderAccounts[0]] : []);
        return;
      }
      setSelectedAccounts(single);
    },
    [orderAccounts],
  );

  return {
    selectedAccounts,
    accounts: orderAccounts,
    handleAccountSelect,
  };
};

export const useAccountsPayload = (selectedAccounts: IAccount[]) => {
  // Fetch accounts with guardianAgentEnabled=true from the API
  const { data: accounts = [] } = useQuery({
    method: AccountControllerService.getAccountBasedOnDataSourceUsingGet,
    params: {
      guardianAgentEnabled: true,
    },
    options: {
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    },
  });

  const orderAccounts = useMemo(() => sortAccountsByName(accounts), [accounts]);

  if (selectedAccounts.length > 0) {
    return toAccountPayload(selectedAccounts.slice(0, 1));
  }

  if (orderAccounts.length > 0) {
    return toAccountPayload([orderAccounts[0]]);
  }

  return [];
};
